const ACCESS_TOKEN_KEY = 'userToken';
const REFRESH_TOKEN_KEY = 'userRefreshToken';
const SESSION_ID_KEY = 'userSessionId';
const SESSION_EXPIRES_AT_KEY = 'userSessionExpiresAt';

function readString(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function sessionStore() {
  return typeof window === 'undefined' ? null : window.sessionStorage;
}

function localStore() {
  return typeof window === 'undefined' ? null : window.localStorage;
}

function setIn(store, key, value) {
  try {
    store?.setItem(key, value);
  } catch {
    // ignore storage failures (private mode, quota)
  }
}

function removeFrom(store, key) {
  try {
    store?.removeItem(key);
  } catch {
    // ignore
  }
}

function readFrom(store, key) {
  try {
    return readString(store?.getItem(key));
  } catch {
    return '';
  }
}

// "Remember me" sessions live in localStorage so they survive browser restarts
// and open in new tabs; default sessions stay per-tab in sessionStorage.
function safeSetItem(key, value, persistent = false) {
  if (typeof window === 'undefined') return;
  const target = persistent ? localStore() : sessionStore();
  const other = persistent ? sessionStore() : localStore();
  if (value) {
    setIn(target, key, value);
    removeFrom(other, key);
  } else {
    removeFrom(target, key);
    removeFrom(other, key);
  }
}

function safeGetItem(key) {
  if (typeof window === 'undefined') return '';
  return readFrom(sessionStore(), key) || readFrom(localStore(), key);
}

function isPersistentSession() {
  return Boolean(readFrom(localStore(), REFRESH_TOKEN_KEY));
}

export function readAuthSession() {
  return {
    refreshToken: safeGetItem(REFRESH_TOKEN_KEY),
    sessionExpiresAt: safeGetItem(SESSION_EXPIRES_AT_KEY),
    sessionId: safeGetItem(SESSION_ID_KEY),
    token: safeGetItem(ACCESS_TOKEN_KEY),
  };
}

export function persistAuthSession(
  { token, refreshToken, sessionId, sessionExpiresAt } = {},
  { remember = false } = {}
) {
  safeSetItem(ACCESS_TOKEN_KEY, readString(token), remember);
  safeSetItem(REFRESH_TOKEN_KEY, readString(refreshToken), remember);
  safeSetItem(SESSION_ID_KEY, readString(sessionId), remember);
  safeSetItem(SESSION_EXPIRES_AT_KEY, readString(sessionExpiresAt), remember);
}

export function updateAccessToken(token) {
  safeSetItem(ACCESS_TOKEN_KEY, readString(token), isPersistentSession());
}

export function clearAuthSessionStorage() {
  safeSetItem(ACCESS_TOKEN_KEY, '');
  safeSetItem(REFRESH_TOKEN_KEY, '');
  safeSetItem(SESSION_ID_KEY, '');
  safeSetItem(SESSION_EXPIRES_AT_KEY, '');
}

export function getAuthStorageKeys() {
  return {
    ACCESS_TOKEN_KEY,
    REFRESH_TOKEN_KEY,
    SESSION_EXPIRES_AT_KEY,
    SESSION_ID_KEY,
  };
}
